import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { category, products, more_products } from "../data/Data";
import { CATEGORY_BY_SLUG } from "../seo/categories";
import {
  getActiveSeason,
  getProductSeason,
  isHiddenSeasonalProduct,
} from "../config/seasons";

/**
 * "Browse Categories" mega-menu.
 *
 * Previously a plain bootstrap list whose links had no `to` prop — every entry
 * rendered as href="/" and went nowhere. Now a single panel showing all
 * categories and their formats at once, each routing to /shop?category=…
 */
export default function Category() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  /**
   * Live product count per category.
   *
   * The menu previously showed hardcoded marketing copy ("13 Fragrances
   * Available") regardless of stock — Dhoop Cone and Sambrani Cup advertised a
   * range with zero listed products, so shoppers clicked through to nothing.
   * Counting from the catalogue keeps the nav honest as products come and go.
   */
  const counts = useMemo(() => {
    const live = [...products, ...more_products].filter(
      (item) => !isHiddenSeasonalProduct(item)
    );
    return category.reduce((acc, cat) => {
      acc[cat.slug] = live.filter((item) => item.category === cat.slug).length;
      return acc;
    }, {});
  }, []);

  const activeSeason = getActiveSeason();
  const featured = useMemo(() => {
    if (!activeSeason) return null;
    return (
      [...products, ...more_products].find(
        (item) =>
          getProductSeason(item) === activeSeason.key && !item.out_of_stock
      ) || null
    );
  }, [activeSeason]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  return (
    <div className="nt-categories" ref={wrapRef}>
      <button
        type="button"
        className={`nt-categories-btn ${open ? "is-open" : ""}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <i className="fas fa-th-large"></i>
        <span>Browse Categories</span>
        <i className={`fas fa-chevron-${open ? "up" : "down"} nt-categories-caret`}></i>
      </button>

      {open && (
        <div className="nt-mega" role="menu">
          {/* Featured tile. Two of the four columns only have two formats, so
              the panel had a large dead area on the right — this puts the
              running season's hero product there instead. */}
          {featured && (
            <Link
              to={`/item/${featured.product_name_key}`}
              className="nt-mega-feature"
              onClick={() => setOpen(false)}
            >
              <span className="nt-mega-feature-badge">{activeSeason.badge}</span>
              <img src={featured.product_img} alt="" aria-hidden="true" />
              <strong>{featured.product_name}</strong>
              <span className="nt-mega-feature-price">
                ₹{featured.price}
                {Number(featured.mainPrice) > Number(featured.price) && (
                  <s>₹{featured.mainPrice}</s>
                )}
              </span>
              <span className="nt-mega-feature-cta">
                Shop the collection
                <i className="fas fa-arrow-right"></i>
              </span>
            </Link>
          )}

          <div className="nt-mega-grid">
            {category.slice(0, 10).map((cat) => {
              const count = counts[cat.slug] || 0;
              return (
                <div
                  className={`nt-mega-col ${count === 0 ? "is-empty" : ""}`}
                  key={cat.id}
                >
                  <Link
                    to={CATEGORY_BY_SLUG[cat.slug]?.path || `/shop?category=${cat.slug}`}
                    className="nt-mega-head"
                    onClick={() => setOpen(false)}
                  >
                    {cat.img && (
                      <span className="nt-mega-thumb">
                        <img src={cat.img} alt="" aria-hidden="true" />
                      </span>
                    )}
                    <span className="nt-mega-head-text">
                      <strong>{cat.category_name}</strong>
                      <em>
                        {count > 0
                          ? `${count} ${count === 1 ? "product" : "products"}`
                          : "Coming online soon"}
                      </em>
                    </span>
                  </Link>

                  {cat.subCat && (
                    <ul className="nt-mega-list">
                      {cat.subCat.map((sub, i) => (
                        <li key={i}>
                          <Link
                            to={`/shop?category=${cat.slug}&format=${encodeURIComponent(
                              sub.category_name.trim()
                            )}`}
                            onClick={() => setOpen(false)}
                          >
                            {sub.category_name.trim()}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          <div className="nt-mega-foot">
            <Link to="/shop" onClick={() => setOpen(false)}>
              View all products
            </Link>
            <Link to="/wholesale" onClick={() => setOpen(false)}>
              Wholesale &amp; bulk pricing
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
